import { useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { useT } from '../i18n';
import { ApiError, isMockApi, useLogin, useVerifyTotp, type CurrentUser } from '../api';
import { Button, Input } from '../ui';
import { Brand } from '../app/Brand';
import { LoginControls } from './LoginControls';
import { useAuthStore, useIsAuthenticated } from './store';
import s from './LoginPage.module.css';

type Step = { kind: 'credentials' } | { kind: 'totp'; challengeId: string; email: string };

const DEMO_ACCOUNTS = [
  { email: 'admin@tassili.dz', roleKey: 'login.demo.owner' },
  { email: 'supervisor@tassili.dz', roleKey: 'login.demo.supervisor' },
  { email: 'tech@tassili.dz', roleKey: 'login.demo.technician' },
  { email: 'billing@tassili.dz', roleKey: 'login.demo.billing' },
] as const;

export function LoginPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const authed = useIsAuthenticated();
  const setSession = useAuthStore((x) => x.setSession);
  const login = useLogin();
  const verify = useVerifyTotp();
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const justLoggedIn = useRef(false);
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (authed && step.kind === 'credentials' && !justLoggedIn.current) return <Navigate to={from} replace />;

  const finish = (token: string, user: CurrentUser) => {
    if (user.memberships.length === 0) {
      setError(t('login.error.noOrg'));
      setStep({ kind: 'credentials' });
      return;
    }
    justLoggedIn.current = true;
    setSession(token, user);
    // Plusieurs organisations : on laisse choisir avant d'entrer
    navigate(user.memberships.length > 1 ? '/select-org' : from, { replace: true, state: { from } });
  };

  const messageFor = (e: unknown) => {
    if (e instanceof ApiError) {
      if (e.code === 'invalid_credentials') return t('login.error.credentials');
      if (e.code === 'mfa_invalid') return t('login.error.totp');
      if (e.code === 'mfa_required') return t('login.error.expired');
    }
    return t('login.error.generic');
  };

  const submitCredentials = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await login.mutateAsync({ email, password });
      if (res.status === 'totp_required') setStep({ kind: 'totp', challengeId: res.challengeId, email });
      else finish(res.token, res.user);
    } catch (err) {
      setError(messageFor(err));
    }
  };

  const submitTotp = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'totp') return;
    setError(null);
    try {
      const res = await verify.mutateAsync({ challengeId: step.challengeId, code });
      finish(res.token, res.user);
    } catch (err) {
      const msg = messageFor(err);
      setError(msg);
      if (err instanceof ApiError && err.code === 'mfa_required') {
        setStep({ kind: 'credentials' });
        setCode('');
      }
    }
  };

  return (
    <div className={s.page}>
      <aside className={s.hero}>
        <Brand />
        <div className={s.pitch}>
          <h1>
            {t('login.hero.title')} <span>{t('login.hero.accent')}</span>
          </h1>
          <p>{t('login.hero.text')}</p>
        </div>
        <ul className={s.points}>
          <li>{t('login.hero.p1')}</li>
          <li>{t('login.hero.p2')}</li>
          <li>{t('login.hero.p3')}</li>
        </ul>
      </aside>

      <main className={s.panel}>
        <div className={s.controls}>
          <LoginControls />
        </div>
        <div className={s.formWrap}>
          {step.kind === 'credentials' ? (
            <form onSubmit={submitCredentials} className={s.form} noValidate>
              <div className={s.head}>
                <h2>{t('login.title')}</h2>
                <p>{t('login.subtitle')}</p>
              </div>
              {error && (
                <p className={s.alert} role="alert">
                  {error}
                </p>
              )}
              <Input label={t('login.email')} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus dir="ltr" />
              <Input label={t('login.password')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required dir="ltr" />
              <Button type="submit" variant="primary" size="lg" loading={login.isPending} iconEnd={<ArrowRight size={18} className="flip-rtl" />} fullWidth disabled={!email || !password}>
                {t('login.submit')}
              </Button>
              <p className={s.demo}>
                {t('login.noAccount')} <Link to="/register">{t('login.createAccount')}</Link>
              </p>
              {isMockApi && (
                <div className={s.demo}>
                  <strong>{t('login.demo.title')}</strong>
                  <p>{t('login.demo.hint')}</p>
                  <ul>
                    {DEMO_ACCOUNTS.map((a) => (
                      <li key={a.email}>
                        <button
                          type="button"
                          className={s.demoBtn}
                          onClick={() => {
                            setEmail(a.email);
                            setPassword('siraj-demo');
                          }}
                        >
                          <code>{a.email}</code>
                          <span>{t(a.roleKey)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={submitTotp} className={s.form} noValidate>
              <div className={s.head}>
                <span className={s.shield}>
                  <ShieldCheck size={24} />
                </span>
                <h2>{t('login.totp.title')}</h2>
                <p>{t('login.totp.subtitle', { email: step.email })}</p>
              </div>
              {error && (
                <p className={s.alert} role="alert">
                  {error}
                </p>
              )}
              <Input
                label={t('login.totp.code')}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className={s.code}
                autoFocus
                required
                dir="ltr"
                hint={isMockApi ? t('login.totp.demoHint') : undefined}
              />
              <Button type="submit" variant="primary" size="lg" loading={verify.isPending} fullWidth disabled={code.length !== 6}>
                {t('login.totp.submit')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setStep({ kind: 'credentials' });
                  setCode('');
                  setError(null);
                }}
              >
                {t('login.totp.back')}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
